'use client';

import { useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Users,
  TrendingUp,
  DollarSign,
  ArrowRight,
  Info,
  CheckCircle2,
  PlusCircle,
} from 'lucide-react';

interface ROICalculatorProps {
  className?: string;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const PRO_MAX_USERS = 10;
const PRO_PLAN = { name: 'Pro', pricePerUser: 79 };
const BUSINESS_PLAN = { name: 'Business', pricePerUser: 99 };

interface CommonTool {
  id: string;
  name: string;
  cost: number;
  category: string;
}

const COMMON_TOOLS: CommonTool[] = [
  { id: 'crm', name: 'AccuLynx / JobNimbus', cost: 250, category: 'CRM Seat' },
  { id: 'satellite', name: 'EagleView / Roofr', cost: 350, category: '10 Reports/mo' },
  { id: 'phone', name: 'Answering Service / CallRail', cost: 200, category: 'Call Handling' },
  { id: 'canvass', name: 'Spotio / SalesRabbit', cost: 150, category: 'Field Canvassing' },
  { id: 'email', name: 'Mailchimp / Marketing', cost: 150, category: 'Automations' },
];

/**
 * Render an interactive monthly ROI estimate in USD using a 15% revenue uplift
 * and the software-cost difference. Seats round up to at least one; teams above
 * ten seats use Business pricing. Negative cost savings reduce the estimate.
 */
export function ROICalculator({ className = '' }: ROICalculatorProps) {
  const reduced = useReducedMotion();
  const [selectedTools, setSelectedTools] = useState<string[]>(['crm', 'satellite', 'phone']);
  const [leads, setLeads] = useState<string>('60');
  const [closeRate, setCloseRate] = useState<string>('18');
  const [avgJobValue, setAvgJobValue] = useState<string>('12500');
  const [teamSize, setTeamSize] = useState<string>('3');

  // Compute spend from selected tools or fallback
  const calculatedSpend = useMemo(() => {
    return selectedTools.reduce((acc, toolId) => {
      const tool = COMMON_TOOLS.find((t) => t.id === toolId);
      return acc + (tool?.cost ?? 0);
    }, 0);
  }, [selectedTools]);

  const toggleTool = (id: string) => {
    setSelectedTools((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const result = useMemo(() => {
    const spend = calculatedSpend;
    const leadCount = Math.max(0, Number(leads) || 0);
    const rate = Math.max(0, Math.min(100, Number(closeRate) || 0));
    const value = Math.max(0, Number(avgJobValue) || 0);
    const seats = Math.max(1, Math.ceil(Number(teamSize) || 1));

    const plan = seats > PRO_MAX_USERS ? BUSINESS_PLAN : PRO_PLAN;
    const rooflyzerCost = plan.pricePerUser * seats;
    const responseUplift = 0.15; // 15% lift from instant quoting + 24/7 Voice AI answering

    const extraRevenue = leadCount * (rate / 100) * value * responseUplift;
    const softwareDelta = spend - rooflyzerCost;
    const totalROI = extraRevenue + softwareDelta;

    return {
      extraRevenue,
      softwareDelta,
      totalROI,
      rooflyzerCost,
      planName: plan.name,
      spend,
    };
  }, [calculatedSpend, leads, closeRate, avgJobValue, teamSize]);

  const inputClassName =
    'w-full rounded-xl bg-white border border-gray-300 px-4 py-2.5 text-sm text-[#181B20] placeholder:text-gray-400 focus:border-[#7950A2] focus:outline-none focus:ring-2 focus:ring-[#7950A2]/20 transition-all font-mono font-medium shadow-2xs';

  const labelClassName = 'block text-xs font-semibold text-gray-700 mb-1.5';

  return (
    <div
      className={`rounded-3xl border border-gray-200 bg-white p-6 sm:p-10 relative overflow-hidden shadow-sm ${className}`}
    >
      <div className="relative z-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2ECF7] border border-[#E4D7EF] text-xs font-semibold text-[#7950A2] font-mono mb-4">
          <TrendingUp className="size-3.5 text-[#7950A2]" />
          <span>TRANSPARENT ROI & TOOL REPLACEMENT MODEL</span>
        </div>

        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#181B20] tracking-tight mb-2">
          Calculate the Cost of Fragmented Software
        </h3>
        <p className="text-[#4B5563] text-sm sm:text-base max-w-2xl mb-8 leading-relaxed">
          Select the apps you currently pay for. See how much you save by replacing them with
          Rooflyzer OS, plus the additional closed revenue from 30-second speed-to-lead.
        </p>

        {/* Quick Tool Selector Pill Row */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 font-mono">
            Click to toggle what you currently pay for:
          </p>
          <div className="flex flex-wrap gap-2.5">
            {COMMON_TOOLS.map((tool) => {
              const active = selectedTools.includes(tool.id);
              return (
                <button
                  key={tool.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleTool(tool.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium border transition-all ${
                    active
                      ? 'bg-[#F2ECF7] border-[#7950A2] text-[#7950A2] font-semibold shadow-2xs ring-1 ring-[#7950A2]/20'
                      : 'bg-white border-gray-200 text-gray-700 hover:text-[#181B20] hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {active ? (
                    <CheckCircle2 className="size-3.5 text-[#7950A2]" />
                  ) : (
                    <PlusCircle className="size-3.5 text-gray-400" />
                  )}
                  <span>{tool.name}</span>
                  <span className={`font-mono ${active ? 'text-[#7950A2]/80' : 'text-gray-500'}`}>
                    (${tool.cost}/mo)
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Inputs Column (6 cols) */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-6 space-y-4 bg-[#F9FAFB] p-6 rounded-2xl border border-gray-200"
          >
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <span className="text-xs font-bold text-[#181B20] uppercase tracking-wider font-mono">
                Your Monthly Metrics
              </span>
              <span className="text-xs font-mono font-bold text-[#7950A2]">
                Current Tech Spend: ${result.spend}/mo
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="roi-leads" className={labelClassName}>
                  <span className="flex items-center gap-1.5">
                    <Users className="size-3.5 text-gray-500" />
                    Leads per month
                  </span>
                </label>
                <input
                  id="roi-leads"
                  type="number"
                  min="0"
                  value={leads}
                  onChange={(e) => setLeads(e.target.value)}
                  className={inputClassName}
                  placeholder="60"
                />
              </div>

              <div>
                <label htmlFor="roi-close-rate" className={labelClassName}>
                  <span className="flex items-center gap-1.5">
                    <TrendingUp className="size-3.5 text-gray-500" />
                    Close rate (%)
                  </span>
                </label>
                <div className="relative">
                  <input
                    id="roi-close-rate"
                    type="number"
                    min="0"
                    max="100"
                    value={closeRate}
                    onChange={(e) => setCloseRate(e.target.value)}
                    className={`${inputClassName} pr-8`}
                    placeholder="18"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 font-medium text-xs font-mono">
                    %
                  </span>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="roi-job-value" className={labelClassName}>
                  <span className="flex items-center gap-1.5">
                    <DollarSign className="size-3.5 text-gray-500" />
                    Average job value ($)
                  </span>
                </label>
                <input
                  id="roi-job-value"
                  type="number"
                  min="0"
                  value={avgJobValue}
                  onChange={(e) => setAvgJobValue(e.target.value)}
                  className={inputClassName}
                  placeholder="12500"
                />
              </div>

              <div>
                <label htmlFor="roi-team-size" className={labelClassName}>
                  <span className="flex items-center gap-1.5">
                    <Users className="size-3.5 text-gray-500" />
                    Team members on Rooflyzer
                  </span>
                </label>
                <input
                  id="roi-team-size"
                  type="number"
                  min="1"
                  step="1"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className={inputClassName}
                  placeholder="3"
                />
              </div>
            </div>

            <p className="text-[11px] text-gray-500 flex items-start gap-1.5 pt-2">
              <Info className="size-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
              Rooflyzer Pro plan ($79/seat/mo; Business at $99/seat/mo above 10 seats) includes
              satellite estimator, 24/7 Voice AI front office, storm canvassing, proposals, and
              unlimited invoicing.
            </p>
          </motion.div>

          {/* Outputs Column (6 cols) */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-6 flex flex-col justify-between space-y-4"
          >
            <div className="grid gap-3.5">
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex items-center justify-between shadow-2xs">
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-[#181B20]">
                    Monthly Software Savings
                  </p>
                  <p className="text-xs text-gray-500">
                    Replacing separate subscriptions vs ${result.rooflyzerCost}/mo Rooflyzer
                  </p>
                </div>
                <p
                  className={`text-xl sm:text-2xl font-bold font-mono ${result.softwareDelta >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}
                >
                  {result.softwareDelta >= 0
                    ? formatCurrency(result.softwareDelta)
                    : `−${formatCurrency(-result.softwareDelta)}`}
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex items-center justify-between shadow-2xs">
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-[#181B20]">
                    Extra Monthly Revenue Lift
                  </p>
                  <p className="text-xs text-gray-500">
                    From 24/7 call answering & instant satellite quotes
                  </p>
                </div>
                <p className="text-xl sm:text-2xl font-bold font-mono text-[#7950A2]">
                  {formatCurrency(result.extraRevenue)}
                </p>
              </div>

              <div className="bg-[#FAF8FC] border-2 border-[#7950A2] rounded-2xl p-5 flex items-center justify-between shadow-xs">
                <div>
                  <p className="text-xs uppercase tracking-wider font-bold text-[#7950A2] mb-0.5 font-mono">
                    Total Monthly Net Value
                  </p>
                  <p className="text-xs text-gray-600">Revenue lift + software consolidation</p>
                </div>
                <p className="text-2xl sm:text-3xl font-extrabold font-mono text-[#181B20]">
                  {formatCurrency(result.totalROI)}
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#F9FAFB] border border-gray-200 text-xs text-gray-600 flex items-center justify-between">
              <span>Estimated annual net value impact:</span>
              <span className="text-[#181B20] font-bold font-mono text-sm ml-1">
                {formatCurrency(result.totalROI * 12)} / year
              </span>
            </div>
          </motion.div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl bg-[#F9FAFB] border border-gray-200">
          <p className="text-xs sm:text-sm text-gray-700 font-medium">
            Need an itemized audit of your current tool contracts and migration path?
          </p>
          <a
            href="/demo"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold bg-[#F7C76C] text-[#181B20] hover:bg-[#eab95a] border border-[#e5b353] shadow-2xs transition-all shrink-0"
          >
            <span>Book a 15-Minute Stack Audit</span>
            <ArrowRight className="size-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
